import { FilteredPortfolioChangedPayload } from 'portfolio/store/actions';
import {
    AggregationKey,
    initialMyPortfolioCapsulesDataState,
    initialMyPortfolioCapsulesSymbolDataState,
    MyPortfolioCapsulesDataState,
    MyPortfolioCapsulesSymbolDataAccumulatedColumn,
    MyPortfolioCapsulesSymbolDataWeightField,
    MyPortfolioCapsulesSymbolDataWeights,
} from 'widgets/myPortfolioCapsules/store/data/MyPortfolioCapsulesDataState';
import { Logger } from '@instinet-analytics/core-lib';
import { PortfolioItem } from 'portfolio/PortfolioItem';
import uniq from 'lodash/uniq';
import { TopicType } from 'widgets/myPortfolioCapsules/topics/TopicType';
import isNil from 'lodash/isNil';
import { getTopic } from 'widgets/myPortfolioCapsules/topics/Topic';

export const noSymbol = '[No Symbol]';

export function computeFullAggregatedData(
    logger: Logger,
    filteredPortfolio: PortfolioItem[],
    topics: TopicType[]
): MyPortfolioCapsulesDataState {
    if (filteredPortfolio.length === 0) {
        return initialMyPortfolioCapsulesDataState();
    }

    logger.debug('Aggregating filtered portfolio');

    const aggregatedData: MyPortfolioCapsulesDataState =
        initialMyPortfolioCapsulesDataState();
    filteredPortfolio.reduce((data, item) => {
        reduceItem(data, item, topics);
        return data;
    }, aggregatedData);

    logger.debug('Completed aggregating filtered portfolio');

    aggregatedData.reset = true;

    return aggregatedData;
}

export function applyChanges(
    changeEvent: FilteredPortfolioChangedPayload,
    prevData: MyPortfolioCapsulesDataState,
    topics: TopicType[]
): MyPortfolioCapsulesDataState {
    const nextData = {
        ...prevData,
    };

    if (changeEvent.updates?.added) {
        for (const newMember of changeEvent.updates.added) {
            reduceItem(nextData, newMember.next!, topics);
        }

        for (const updatedMember of changeEvent.updates.updated) {
            deduct(nextData, updatedMember.prev!, topics);
            reduceItem(nextData, updatedMember.next!, topics);
        }

        for (const removedMember of changeEvent.updates.removed) {
            deduct(nextData, removedMember.prev!, topics);
        }
    }
    nextData.reset = false;
    return nextData;
}

function reduceItem(
    data: MyPortfolioCapsulesDataState,
    item: PortfolioItem,
    topics: TopicType[],
    factor: 1 | -1 = 1
) {
    reduceAggregatedItem({
        aggregationKey: 'allValid',
        data,
        item,
        topics,
        factor,
    });
    reduceAggregatedItem({
        aggregationKey: 'excludingDone',
        data,
        item,
        topics,
        factor,
    });
    reduceAggregatedItem({
        aggregationKey: 'onlyDone',
        data,
        item,
        topics,
        factor,
    });
}

function reduceAggregatedItem({
    aggregationKey,
    data: dataState,
    item,
    topics,
    factor,
}: {
    aggregationKey: AggregationKey;
    data: MyPortfolioCapsulesDataState;
    item: PortfolioItem;
    topics: TopicType[];
    factor: 1 | -1;
}) {
    if (!included(item, aggregationKey)) {
        return;
    }

    const data = dataState[aggregationKey];

    data.totals.count = data.totals.count + factor;

    const symbol = sanitizeSymbol(item.symbol);

    if (!data.bySymbol[symbol]) {
        data.bySymbol[symbol] = initialMyPortfolioCapsulesSymbolDataState();
    }

    const symbolData = data.bySymbol[symbol];

    symbolData.count = symbolData.count + factor;
    symbolData.accumulated['qty'] = symbolData.accumulated['qty'] + item.qty;
    symbolData.accumulated['qtyTraded'] =
        symbolData.accumulated['qtyTraded'] + item.qtyTraded;
    symbolData.accumulated['qtyRemaining'] =
        symbolData.accumulated['qtyRemaining'] + item.qtyRemaining;

    const accumulatedFields = getAccumulatedFields(topics);

    for (const accumulatedField of accumulatedFields) {
        const value = item[accumulatedField] ?? 0;

        const existing = symbolData.accumulated[accumulatedField] ?? 0;
        symbolData.accumulated[accumulatedField] = existing + value * factor;

        const existingTotal = data.totals.accumulated[accumulatedField] ?? 0;
        data.totals.accumulated[accumulatedField] =
            existingTotal + value * factor;
    }

    const weights = getWeights(topics);
    const itemWeights: MyPortfolioCapsulesSymbolDataWeights = {};

    for (const weight of weights) {
        if (weight === 'tradedQtyWeightedByAveragePrice') {
            itemWeights.tradedQtyWeightedByAveragePrice =
                (item.qtyTraded ?? 0) * item.averagePrice * factor;
            symbolData.weights.tradedQtyWeightedByAveragePrice =
                (symbolData.weights.tradedQtyWeightedByAveragePrice ?? 0) +
                itemWeights.tradedQtyWeightedByAveragePrice;
            data.totals.weights.tradedQtyWeightedByAveragePrice =
                (data.totals.weights.tradedQtyWeightedByAveragePrice ?? 0) +
                itemWeights.tradedQtyWeightedByAveragePrice;
        }
    }

    for (const topicType of topics) {
        const topic = getTopic(topicType);
        topic.calculateDerivedValues &&
            topic.calculateDerivedValues(item, symbolData, itemWeights, factor);
    }
}

function deduct(
    data: MyPortfolioCapsulesDataState,
    item: PortfolioItem,
    topics: TopicType[]
) {
    reduceItem(data, item, topics, -1);
}

function getAccumulatedFields(
    topics: TopicType[]
): MyPortfolioCapsulesSymbolDataAccumulatedColumn[] {
    const ret: MyPortfolioCapsulesSymbolDataAccumulatedColumn[] = [];
    for (const topicType of topics) {
        const topic = getTopic(topicType);
        ret.push(...topic.accumulatedColumns);
    }

    return uniq(ret);
}

function getWeights(
    topics: TopicType[]
): MyPortfolioCapsulesSymbolDataWeightField[] {
    const ret: MyPortfolioCapsulesSymbolDataWeightField[] = [];
    for (const topicType of topics) {
        const topic = getTopic(topicType);
        ret.push(...topic.weights);
    }

    return uniq(ret);
}

function sanitizeSymbol(symbol: string | null | undefined): string {
    if (isNil(symbol) || symbol?.trim() === '') {
        return noSymbol;
    }
    return symbol;
}

function included(
    item: PortfolioItem,
    aggregationKey: AggregationKey
): boolean {
    const isDone = item.state?.toLowerCase().trim() === 'done';
    switch (aggregationKey) {
        case 'onlyDone':
            return isDone;
        case 'allValid':
            return isValidOrder(item);
        case 'excludingDone':
            return isValidOrder(item) && !isDone;
    }
}

function isValidOrder(item: PortfolioItem) {
    switch (item.state?.toLowerCase().trim() ?? '') {
        case 'cancelled':
        case 'rejected':
        case 'cancelling':
        case 'invalid':
            return false;
        default:
            return true;
    }
}
